import { useCallback, useState } from 'react';

/**
 * Small wrapper around the `setLoading → try → catch → setError → finally`
 * pattern that every async wizard transition was repeating verbatim.
 *
 * Usage:
 *   const { loading, error, run, clearError } = useAsyncStep();
 *   const onNext = () => run(async () => { ... }, 'Friendly fallback message');
 *
 * Returns:
 *   - loading: boolean while the async fn is pending
 *   - error:   string message (empty when none)
 *   - run:     (asyncFn, fallbackErrorMessage?) => Promise<void>
 *              Swallows the rejection and routes it through setError.
 *   - clearError: () => void
 */
export function useAsyncStep() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (asyncFn, fallbackMessage) => {
    setError('');
    setLoading(true);
    try {
      await asyncFn();
    } catch (err) {
      setError(err?.message || fallbackMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(''), []);

  return { loading, error, setError, clearError, run };
}
